import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  KeyIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import IconButton from "./IconButton";
import ErrorBanner from "../../../ui/ErrorBanner";
import {
  getPasswordRequirements,
  getPasswordStrength,
  getLockColor,
  getConfirmLockColor,
} from "../../../utils/passwordValidation";

export default function ChangePasswordModal({ onClose, onSubmit }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const requirements = useMemo(() => getPasswordRequirements(newPassword), [newPassword]);
  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  // Lock colors
  const lockColorNew = useMemo(() => getLockColor(newPassword, requirements, strength), [newPassword, requirements, strength]);
  const lockColorCurrent = "text-text-muted"; // neutral
  const lockColorConfirm = useMemo(() => getConfirmLockColor(confirmPassword, newPassword), [confirmPassword, newPassword]);

  const newEqualsCurrent =
    newPassword && currentPassword && newPassword === currentPassword;

  const canSubmit =
    currentPassword.length > 0 &&
    requirements.allOk &&
    newPassword === confirmPassword &&
    !newEqualsCurrent;

  const handleSubmit = async () => {
    setError("");
    if (!canSubmit) {
      setError("Please fix the form errors first.");
      return;
    }
    try {
      setSubmitting(true);
      await onSubmit({ currentPassword, newPassword, confirmPassword });
    } catch (e) {
      setError("Unable to change password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-2xl bg-surface-elevated border border-border shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-border bg-surface">
          <div className="flex items-center gap-2 text-text-primary">
            <KeyIcon className="h-5 w-5 text-primary" />
            <h3 className="text-sm font-semibold">Change password</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-surface-sunken"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-text-secondary" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Compact guidance block */}
          <div className="text-[13px] leading-relaxed text-text-secondary bg-surface border border-border rounded-lg px-3 py-2">
            <p className="mb-1">
              To change your password, please fill in the fields below.
            </p>
            <p>
              Your password must contain at least 8 characters and include at
              least one uppercase letter, one lowercase letter, one number, and
              one special character.
            </p>
          </div>

          {error && (
            <ErrorBanner
              message={error}
              onRetry={handleSubmit}
              onDismiss={() => setError("")}
            />
          )}

          {/* Current password */}
          <div>
            <label className="block text-sm font-medium text-text-secondary">
              Current password
            </label>
            <div className="mt-1 relative">
              <LockClosedIcon
                className={`absolute inset-y-0 left-2 my-auto h-4 w-4 ${lockColorCurrent}`}
                aria-hidden="true"
              />
              <input
                type={showCur ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-surface-elevated rounded-lg border border-border-strong px-3 py-2 pr-10 pl-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-600"
              />
              <IconButton
                active={showCur}
                label={showCur ? "Hide password" : "Show password"}
                onClick={() => setShowCur((s) => !s)}
              >
                {showCur ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </IconButton>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block text-sm font-medium text-text-secondary">
              New password
            </label>
            <div className="mt-1 relative">
              <LockClosedIcon
                className={`absolute inset-y-0 left-2 my-auto h-4 w-4 ${lockColorNew}`}
                aria-hidden="true"
              />
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-surface-elevated rounded-lg border border-border-strong px-3 py-2 pr-10 pl-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-600"
              />
              <IconButton
                active={showNew}
                label={showNew ? "Hide password" : "Show password"}
                onClick={() => setShowNew((s) => !s)}
              >
                {showNew ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </IconButton>
            </div>

            {/* Inline unmet criteria summary */}
            {newPassword && (
              <div className="mt-2 text-xs">
                {!requirements.allOk && (
                  <div className="rounded-md bg-amber-50 border border-border-amber-200 text-amber-800 px-3 py-2">
                    <p className="font-medium mb-1">
                      Password needs the following:
                    </p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {requirements.list
                        .filter((r) => !r.ok)
                        .map((r) => (
                          <li key={r.id}>{r.label}</li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Warning if new === current */}
            {newEqualsCurrent && (
              <p className="mt-2 text-xs text-destructive">
                New password must be different from your current password.
              </p>
            )}
          </div>

          {/* Confirm new password */}
          <div>
            <label className="block text-sm font-medium text-text-secondary">
              Confirm new password
            </label>
            <div className="mt-1 relative">
              <LockClosedIcon
                className={`absolute inset-y-0 left-2 my-auto h-4 w-4 ${lockColorConfirm}`}
                aria-hidden="true"
              />
              <input
                type={showConf ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-surface-elevated rounded-lg border border-border-strong px-3 py-2 pr-10 pl-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-600"
              />
              <IconButton
                active={showConf}
                label={showConf ? "Hide password" : "Show password"}
                onClick={() => setShowConf((s) => !s)}
              >
                {showConf ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </IconButton>
            </div>
            {confirmPassword && confirmPassword !== newPassword && (
              <p className="mt-1 text-xs text-destructive">
                Passwords do not match.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              className="px-3 py-1.5 rounded-md bg-surface-sunken text-text-secondary border border-border-strong text-sm hover:bg-surface"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1.5 rounded-md bg-primary hover:bg-primary-hover text-text-inverse text-sm hover:bg-primary disabled:opacity-60"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? "Updating…" : "Update password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
